import { Injectable } from '@angular/core'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import { User } from './models/user.entity'
import {
  Observable,
  combineLatest,
  concat,
  concatMap,
  delay,
  from,
  last,
  map,
  of,
  scan,
  switchMap,
} from 'rxjs'
import { UsersService } from './users.service'
import { Role, RolesService } from '../roles'
import { Page } from '../../models/util.model'
import { parseUsersPageKeys } from './libs/user.lib'
import { AuthService } from '../auth'
import { difference, uniqBy } from 'lodash'
import { Permissions } from '../policies'
import {
  parseUserModal,
  parseUserNotification,
  UserModalCode,
  UserNotificationCode,
} from './libs/user-notification.lib'
import {
  ModalResponse,
  NgModalServiceInterface,
} from '../../models/modal.model'
import { UserSearchParams } from './models/user-search.model'
import { UsersListingData, UsersListingPage } from './models/user-listing.model'

@Injectable({
  providedIn: 'root',
})
export class UsersRepository {
  private paginationLimit = 200

  constructor(
    private usersService: UsersService,
    private rolesService: RolesService,
    private authService: AuthService,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search users and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search users
   */
  searchUsers$(
    searchParams: UserSearchParams,
    tenantId: string,
    returnAll?: boolean,
  ): Observable<UsersListingPage> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.paginationLimit

    return needsPagination
      ? this._paginateUsers$(searchParams, tenantId)
      : this._searchUsers$(searchParams, tenantId, returnAll)
  }

  /**
   * Search users with relative external data
   * @param searchParams - the search params
   * @param fields - the users fields
   * @param listingData - the already loaded listing data
   * @returns the observable<UsersListingPage> for search users
   */
  private _searchUsers$(
    searchParams: UserSearchParams,
    tenantId: string,
    returnAll?: boolean,
  ): Observable<UsersListingPage> {
    return this.usersService
      .search$(searchParams, returnAll)
      .pipe(
        switchMap((page) =>
          this._loadExtData$(page, tenantId).pipe(
            map((extData) => ({ ...page, extData })),
          ),
        ),
      )
  }

  /**
   * Search more than 200 users with relative external data
   * @param searchParams - the search params
   * @param fields - the users fields
   * @param listingData - the already loaded listing data
   * @returns the observable<UsersListingPage> for search users
   */
  private _paginateUsers$(
    searchParams: UserSearchParams,
    tenantId: string,
  ): Observable<UsersListingPage> {
    const totalCount = searchParams.limit || this.paginationLimit
    const pagesCount = Math.ceil(totalCount / this.paginationLimit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.paginationLimit,
        offset: i * this.paginationLimit,
      })),
    ).pipe(
      concatMap((pageParams) => this._searchUsers$(pageParams, tenantId)),
      scan<UsersListingPage, UsersListingPage>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
          extData: { ...acc.extData, ...current.extData },
        }),
        { totalCount: 0, data: [], extData: {} } as UsersListingPage,
      ),
      last(),
    )
  }

  /**
   * Load page external data
   * @param page - the users page
   * @param fields - the fields
   * @returns the observable for load external data
   */
  private _loadExtData$(
    page: Page<User>,
    tenantId: string,
  ): Observable<UsersListingData> {
    const extDataKeys = parseUsersPageKeys(page, tenantId)
    const obs$: Partial<{
      roles: Observable<Role[]>
    }> = {}

    if (extDataKeys.roleIds) {
      obs$['roles'] = this.rolesService.store$(extDataKeys.roleIds, [])
    }

    if (!Object.keys(obs$).length) {
      return of({} as UsersListingData)
    }

    return combineLatest(obs$).pipe(
      map((results) => {
        const extData: UsersListingData = {}
        if (results.roles) extData.roles = results.roles
        return extData
      }),
    )
  }

  /**
   * Invite a user and assign him roles
   * @param username - the username
   * @param roleIds - the role IDs
   * @returns the observable for invite and assign roles
   */
  inviteUser$(username: string, roleIds: string[]) {
    return this.authService
      .invite$(username)
      .pipe(switchMap((user) => this.assignRoles$(roleIds, [user._id])))
  }

  /**
   * Assign roles to users
   * @param roleIds - the roles IDs
   * @param userIds - the users IDs
   * @returns the observable to assign roles to users
   */
  assignRoles$(roleIds: string[], userIds: string[]): Observable<void | null> {
    if (!roleIds.length || !userIds.length) {
      return of(null)
    }

    return concat(
      ...roleIds.map((roleId) => this.rolesService.assign$(roleId, userIds)),
    ).pipe(last())
  }

  /**
   * Remove roles from users
   * @param roleIds - the roles IDs
   * @param userIds - the users IDs
   * @returns the observable to remove roles from users
   */
  removeRoles$(roleIds: string[], userIds: string[]): Observable<void | null> {
    if (!roleIds.length || !userIds.length) {
      return of(null)
    }

    return concat(
      ...roleIds.map((roleId) => this.rolesService.remove$(roleId, userIds)),
    ).pipe(last())
  }

  /**
   * Update user roles
   * @param user - the user to update
   * @param tenantId - the tenant ID
   * @param roleIds - the role IDs
   * @returns the observable for update the user roles
   */
  updateUserRoles$(
    user: User,
    tenantId: string,
    roleIds: string[],
  ): Observable<{ user: User; permissions: Permissions }> {
    const userRoles = (user.roles || [])
      .filter((r) => r.tenantId === tenantId)
      .map((r) => r._id)

    const roleToAssign = difference(roleIds, userRoles)
    const roleToRemove = difference(userRoles, roleIds)

    if (!roleToAssign.length && !roleToRemove.length) {
      return combineLatest({
        user: of(user),
        permissions: this.usersService.getPermissions$(user._id),
      })
    }

    return concat(
      ...roleToAssign.map((roleId) =>
        this.rolesService.assign$(roleId, [user._id]),
      ),
      ...roleToRemove.map((roleId) =>
        this.rolesService.remove$(roleId, [user._id]),
      ),
    ).pipe(
      last(),
      delay(6000),
      switchMap(() =>
        combineLatest({
          user: this.usersService.read$(user._id),
          permissions: this.usersService.getPermissions$(user._id),
        }),
      ),
    )
  }

  notify(options: { code?: UserNotificationCode; message?: string }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseUserNotification(options.code),
    )
  }

  async alert(code: UserModalCode, user?: User): Promise<ModalResponse> {
    const settings = parseUserModal(code, user)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
